import React, { useState } from 'react';
import styled from 'styled-components';

import { MessagingChatMessage } from './message.types';
import { useTheme } from '../../../../../../styles';
import { alpha, Stack, Tooltip } from '@mui/material';
import { FullscreenImage, FullscreenImageOverlay } from '@components/Docs';

const Container = styled.div<{ isSender: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ isSender }) => (!isSender ? 'flex-end' : 'flex-start')};
  margin: 0.75rem 0;
  margin-top: 1.25rem;
`;

const Bubble = styled.div<{ isSender: boolean; isError?: boolean }>`
  background-color: ${({ theme, isSender, isError }) =>
    isError ? theme.telnyx.error : !isSender ? theme.primary : theme.background.standard};

  color: ${({ theme, isSender, isError }) => (!isSender || isError ? 'white' : theme.text.standard)};
  border: 1px solid ${({ theme }) => theme.line.light};
  padding: 1rem;
  border-radius: 10px;
  max-width: 80%;
  word-wrap: break-word;
  font-size: 15px;

  ::selection {
    background-color: ${({ isSender }) => (!isSender ? 'white' : undefined)};
    color: ${({ isSender }) => (!isSender ? 'black' : undefined)};
  }
`;

const Timestamp = styled.div`
  font-size: 12px;
  color: ${({ theme }) => alpha(theme.text.standard, 0.6)};

  &:hover {
    cursor: pointer;
  }
`;

const SenderPhoneNumber = styled.div`
  font-size: 12px;
  color: ${({ theme }) => alpha(theme.text.standard, 0.6)};
  margin-right: 0.3rem;
  padding-right: 0.3rem;
  border-right: 1px solid ${({ theme }) => theme.line.light};
`;

const ImageGrid = styled.div<{ isSender: boolean }>`
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 0.75rem;
  overflow: hidden;
  width: 100%;
  margin-top: 0.5rem;
  direction: ${({ isSender }) => (!isSender ? 'rtl' : 'ltr')};
`;

const Image = styled.img`
  aspect-ratio: 1;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.line.light};
  object-fit: cover;
  height: 50px;
  overflow: hidden;

  &:hover {
    cursor: pointer;
  }
`;

interface Props {
  message: MessagingChatMessage;
}

export const MessageChat: React.FC<Props> = ({ message }) => {
  const theme = useTheme();

  const [fullscreenSrc, setFullscreenSrc] = useState('');

  const isSender = message.source === 'sender';
  const isError = message.source === 'sender' && message?.error;

  const closeFullscreen = () => {
    setFullscreenSrc('');
  };

  return (
    <Container isSender={isSender}>
      {isError ? (
        <Bubble theme={theme} isSender={isSender} isError>
          {message.error}
        </Bubble>
      ) : (
        <Bubble theme={theme} isSender={isSender}>
          {message.text}
        </Bubble>
      )}
      {!isError && message?.media_urls?.length > 0 && (
        <ImageGrid isSender={isSender}>
          {message.media_urls.map((url, index) => (
            <Image
              key={index}
              src={url}
              theme={theme}
              alt={`Media Not Found (${url})`}
              onClick={() => setFullscreenSrc(url)}
            />
          ))}
        </ImageGrid>
      )}
      {!isError && (
        <Stack direction='row' alignItems='center' mt={1}>
          {isSender && <SenderPhoneNumber>{message.from}</SenderPhoneNumber>}
          <Tooltip arrow placement='top' title={new Date(message.sent_at).toUTCString()}>
            <Timestamp theme={theme}>
              {new Date(message.sent_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </Timestamp>
          </Tooltip>
        </Stack>
      )}
      {fullscreenSrc && (
        <FullscreenImageOverlay onClick={closeFullscreen}>
          <FullscreenImage
            src={fullscreenSrc}
            onClick={(e) => {
              e.stopPropagation();
              setFullscreenSrc('');
            }}
            alt='Fullscreen Image'
          />
        </FullscreenImageOverlay>
      )}
    </Container>
  );
};
